import React, { useContext, useEffect, useRef, useState } from 'react';
import { UserContext } from '../ContextProviders';
import { listNovaDocuments, NOVA_DOCUMENT_ACCEPT, NOVA_DOCUMENT_FORMATS_LABEL, NOVA_DOCUMENT_LIMITS_LABEL } from '@/api';
import { useNotification } from '@/components';
import { NovaDocumentObject } from '@/types';
import { novaThemes, NovaThemeName } from './novaTheme';
import { NovaUploadIcon, NovaFolderIcon, NovaChevronRightIcon } from './novaIcons';

const formatBytes = (bytes: number): string => {
  if (!bytes) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / k ** i).toFixed(1))} ${sizes[i]}`;
};

interface NovaAttachMenuProps {
  theme: NovaThemeName;
  onClose: () => void;
  onAttachFiles: (files: File[]) => void;
  userProfileId?: string;
  onDocumentAttached: (relativeName: string) => void;
}

// The "+" button's popover — lets a question pull in a brand new document (browsed
// straight from the OS picker) or reuse one already indexed from an earlier chat,
// without re-uploading it. Matches the Nova buildout handoff's "Add to this
// question" panel.
const NovaAttachMenu: React.FC<NovaAttachMenuProps> = ({
  theme,
  onClose,
  onAttachFiles,
  userProfileId,
  onDocumentAttached,
}) => {
  const surfaces = novaThemes[theme];
  const aadGuid = useContext(UserContext)?.userId || '';
  const { showNotification } = useNotification();
  const [view, setView] = useState<'root' | 'documents'>('root');
  const [documents, setDocuments] = useState<NovaDocumentObject[] | null>(null);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const attachInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (documents !== null || documentsLoading || !userProfileId || !aadGuid) return;
    setDocumentsLoading(true);
    listNovaDocuments(aadGuid, userProfileId)
      .then((resp) => {
        if (resp.succeeded) setDocuments(resp.documents ?? []);
        else setDocuments([]);
      })
      .catch((error) => {
        console.error('Failed to load Nova documents:', error);
        setDocuments([]);
      })
      .finally(() => setDocumentsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfileId, aadGuid]);

  // Nova documents live flat under `Stylus-Ava-User-docs/${userProfileId}/` — AVA
  // resolves file_paths there regardless of which chat sent them, so reusing a
  // document from a past chat just means reusing its relativeName; no copy needed.
  const handleAttachExisting = (doc: NovaDocumentObject) => {
    const relativeName = doc.key.split('/').pop();
    if (!relativeName) {
      showNotification(`Could not attach ${doc.name}.`, 'error');
      return;
    }
    onDocumentAttached(relativeName);
    onClose();
  };

  const rowClass =
    'flex w-full cursor-pointer items-start gap-2.5 rounded-lg p-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 hover:bg-[var(--nova-row-hover)]';
  const rowHoverStyle = { '--nova-row-hover': surfaces.surfaces.field } as React.CSSProperties;

  const iconSquare = (children: React.ReactNode, accent?: boolean) => (
    <span
      className="flex h-7 w-7 flex-none items-center justify-center rounded-lg"
      style={{
        background: accent ? surfaces.actionAccent : surfaces.surfaces.activeRow,
        color: accent ? surfaces.ink.onGold : surfaces.ink.heading,
      }}
    >
      {children}
    </span>
  );

  return (
    <div
      className="w-[280px] overflow-hidden rounded-xl border shadow-xl"
      style={{ background: surfaces.surfaces.card, borderColor: surfaces.surfaces.panelBorder }}
    >
      <input
        ref={attachInputRef}
        type="file"
        multiple
        accept={NOVA_DOCUMENT_ACCEPT}
        className="hidden"
        onChange={(e) => {
          if (e.target.files && e.target.files.length > 0) onAttachFiles(Array.from(e.target.files));
          e.target.value = '';
          onClose();
        }}
      />

      {view === 'root' ? (
        <>
          <div className="px-3 pt-3 pb-1 text-[10px] tracking-[0.12em] uppercase" style={{ color: surfaces.ink.secondary }}>
            Add to this question
          </div>
          <div className="flex flex-col gap-0.5 px-1.5 pb-1.5">
            <button type="button" className={rowClass} style={{ ...rowHoverStyle, color: surfaces.ink.heading }} onClick={() => attachInputRef.current?.click()}>
              {iconSquare(<NovaUploadIcon className="h-3.5 w-3.5" />, true)}
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="text-[13px] font-semibold">Add document</span>
                <span className="text-[11.5px]" style={{ color: surfaces.ink.secondary }}>
                  Browse your computer — {NOVA_DOCUMENT_FORMATS_LABEL}
                </span>
                <span className="text-[11.5px]" style={{ color: surfaces.ink.secondary }}>
                  {NOVA_DOCUMENT_LIMITS_LABEL}
                </span>
              </span>
            </button>

            <button
              type="button"
              className={rowClass}
              style={{ ...rowHoverStyle, color: surfaces.ink.heading }}
              disabled={!userProfileId}
              onClick={() => setView('documents')}
            >
              {iconSquare(<NovaFolderIcon className="h-3.5 w-3.5" />)}
              <span className="flex min-w-0 flex-col gap-0.5">
                <span className="text-[13px] font-semibold">From your Nova Documents</span>
                <span className="text-[11.5px]" style={{ color: surfaces.ink.secondary }}>
                  {documentsLoading || documents === null
                    ? 'Loading your documents…'
                    : `${documents.length} document${documents.length === 1 ? '' : 's'} already indexed`}
                </span>
              </span>
            </button>
          </div>
          <div
            className="border-t px-3 py-2.5 text-[11px] leading-[15px]"
            style={{ borderColor: surfaces.surfaces.hairline, color: surfaces.ink.placeholder }}
          >
            Your documents stay private to you and are never used to train Nova.ai.
          </div>
        </>
      ) : (
        <>
          <button
            type="button"
            className="flex w-full cursor-pointer items-center gap-1.5 px-3 pt-3 pb-1.5 text-left"
            style={{ color: surfaces.ink.secondary }}
            onClick={() => setView('root')}
          >
            <NovaChevronRightIcon className="h-3 w-3 rotate-180" />
            <span className="text-[10px] tracking-[0.12em] uppercase">From your Nova Documents</span>
          </button>
          <div className="max-h-[240px] overflow-y-auto px-1.5 pb-1.5" data-lenis-prevent>
            {documentsLoading && <div className="p-2.5 text-[12px]" style={{ color: surfaces.ink.secondary }}>Loading…</div>}
            {!documentsLoading && documents?.length === 0 && (
              <div className="p-2.5 text-[12px]" style={{ color: surfaces.ink.secondary }}>
                No documents indexed yet.
              </div>
            )}
            {documents?.map((doc) => (
              <button
                key={doc.key}
                type="button"
                className="flex w-full cursor-pointer flex-col gap-0.5 rounded-lg p-2 text-left transition-colors hover:bg-[var(--nova-row-hover)]"
                style={{ ...rowHoverStyle, color: surfaces.ink.heading }}
                onClick={() => handleAttachExisting(doc)}
              >
                <span className="truncate text-[12.5px] font-medium">{doc.name}</span>
                <span className="text-[11px]" style={{ color: surfaces.ink.placeholder }}>{formatBytes(doc.size)}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default NovaAttachMenu;
